import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { getErrorMessage } from '../utils/errorHandler';
import { claimTokenFromSearch, resumeClaimByToken } from '../utils/claimTokenResume';
import { useSnackbar } from '../contexts/SnackbarContext';

type Options = {
  allowedStatuses?: string[];
  fallbackPath?: string;
};

export const useClaimTokenResume = (options: Options = {}) => {
  const { allowedStatuses, fallbackPath = '/' } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { showError } = useSnackbar();
  const claimId = useSelector((state: RootState) => state.claim.claimId);
  const claimStatus = useSelector((state: RootState) => state.claim.claimStatus);
  const claimToken = claimTokenFromSearch(location.search);
  const fromClaimStatus = new URLSearchParams(location.search).get('from') === 'claim-status';
  const needsTokenResume = Boolean(claimToken && !claimId);
  const [isResuming, setIsResuming] = useState(needsTokenResume);
  const [resumeError, setResumeError] = useState('');
  const resumeRequestId = useRef(0);

  useEffect(() => {
    if (!claimToken && !claimId) {
      navigate(fallbackPath, { replace: true });
    }
  }, [claimId, claimToken, fallbackPath, navigate]);

  useEffect(() => {
    if (claimId || !claimToken) {
      setIsResuming(false);
      setResumeError('');
      return;
    }

    const requestId = ++resumeRequestId.current;
    setIsResuming(true);
    setResumeError('');

    resumeClaimByToken(claimToken, dispatch)
      .then((existingClaim) => {
        if (requestId !== resumeRequestId.current) {
          return;
        }
        const status = String(existingClaim.status ?? '').toUpperCase();
        if (allowedStatuses && !allowedStatuses.includes(status)) {
          navigate(fallbackPath, { replace: true });
        }
      })
      .catch((err) => {
        if (requestId !== resumeRequestId.current) {
          return;
        }
        const message = getErrorMessage(err) || 'Unable to resume claim from this link.';
        setResumeError(message);
        showError(message);
      })
      .finally(() => {
        if (requestId === resumeRequestId.current) {
          setIsResuming(false);
        }
      });
  }, [allowedStatuses, claimId, claimToken, dispatch, fallbackPath, navigate, showError]);

  return {
    claimToken,
    fromClaimStatus,
    isResuming: needsTokenResume && isResuming,
    resumeError,
    isClaimReady: Boolean(claimId),
    claimStatus,
    isIncomplete: claimStatus === 'INCOMPLETE',
  };
};
